import React, { useEffect, useState, useContext } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { HiPlus, HiPencil, HiArrowLeft, HiEye } from "react-icons/hi";
import { DataGrid, gridPageCountSelector, gridPageSelector, useGridApiContext, useGridSelector } from "@mui/x-data-grid";
import Pagination from "@mui/material/Pagination";
import PaginationItem from "@mui/material/PaginationItem";

import ToggleSwitch from "../../components/ToggleSwitch";
import GeneralModal from "../../helpers/GeneralModal";
import { createModal } from "react-modal-promise";
import LogicContext from "../../Context/LogicContext";

import { SetGroupEdit, SetGroupImageEdit } from "../../store/itemGroupAtom";
import { itemsGroupService } from "../../services/itemGroup.service";
import { formatCustomNumber } from "../../helpers/formatCustomNumber";

import ItemGroupDetailsScroll from "./ItemGroupDetailsScroll";
import ItemGroupImageDisplay from "./ItemGroupImagedisplay";

function CustomPagination() {
  const apiRef = useGridApiContext();
  const page = useGridSelector(apiRef, gridPageSelector);
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);

  return (
    <Pagination
      color="primary"
      variant="outlined"
      shape="rounded"
      page={page + 1}
      count={pageCount}
      renderItem={(item) => <PaginationItem {...item} disableRipple />}
      onChange={(event, value) => apiRef.current.setPage(value - 1)}
    />
  );
}

export default function ItemGroupDetailsPage() {
  const navigate = useNavigate();
  const { item_group_id } = useParams();
  const { pathname } = useLocation();
  const logicContext = useContext(LogicContext);

  const setGroupToEdit = SetGroupEdit();
  const setGroupImageEdit = SetGroupImageEdit();

  const [itemGroupDetails, setItemGroupDetails] = useState({});
  const [imageList, setImageList] = useState([]);
  const [tableRows, setTableRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [currModuleId, currModuleName, currUserMenu] = pathname.split("/").slice(1, 4);

  const myPromiseModal = createModal(GeneralModal);

  useEffect(() => {
    document.title = "Item Group | Details";
    loadGroupDetails(item_group_id);
  }, [item_group_id]);

  const loadGroupDetails = async (groupId) => {
    setLoading(true);
    try {
      const res = await itemsGroupService.getGroupDetails(groupId);
      const groupInfo = res.data?.group_info?.[0];
      const pictures = res.data?.picture_info || [];

      if (groupInfo) setItemGroupDetails(groupInfo);
      setImageList(pictures);
    } catch (err) {
      logicContext.showToast("Failed to load item group details", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchTableData = async () => {
      try {
        const res = await itemsGroupService.getGroupTabledata(item_group_id);
        const formatted = res.data.map((item) => ({
          ...item,
          id: item.Item_Id,
          Sales_Rate: formatCustomNumber(item?.Sales_Rate),
        }));
        setTableRows(formatted);
      } catch (err) {
        logicContext.showToast("Failed to load item list", "error");
      }
    };

    if (item_group_id) fetchTableData();
  }, [item_group_id]);

  const goBack = () => navigate(-1);

  const confirmAndEdit = async () => {
    const confirmed = await myPromiseModal({
      title: "Edit Item Group",
      body: "Would you like to modify this item group?",
      showInput: false,
    });

    if (confirmed?.decision) {
      setGroupToEdit(itemGroupDetails);
      setGroupImageEdit(imageList);
      navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/add-item-group`);
    }
  };

  const goToNewGroup = () => {
    navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/add-item-group`);
  };

  const toggleGroupStatus = (shouldActivate) => {
    const statusId = shouldActivate ? 1 : 2;
    const statusName = shouldActivate ? "Active" : "Inactive";

    // Optimistic update
    setItemGroupDetails((prev) => ({
      ...prev,
      Item_Group_Status_Id: statusId,
      Item_Group_Status_Name: statusName,
    }));

    const xmlString = `<a><refno>${itemGroupDetails.Item_Group_Id}</refno></a>`;
    const apiCall = shouldActivate
      ? itemsGroupService.setGroupItemActive
      : itemsGroupService.setGroupItemInActive;

    apiCall(`<root>${xmlString}</root>`)
      .then((res) => {
        logicContext.showToast(res.data[0]?.message_text || "Status updated", "success");
      })
      .catch(() => {
        logicContext.showToast("Failed to update status", "error");
        // Rollback on error
        setItemGroupDetails((prev) => ({
          ...prev,
          Item_Group_Status_Id: shouldActivate ? 2 : 1,
          Item_Group_Status_Name: shouldActivate ? "Inactive" : "Active",
        }));
      });
  };

  const isActive = itemGroupDetails?.Item_Group_Status_Id === 1;

  const columns = [
    { field: "Item_Id", headerName: "ID", width: 100 },
    { field: "Item_Name", headerName: "Name", width: 220, flex: 1 },
    { field: "Item_Sku", headerName: "SKU", width: 140 },
    { field: "Item_Category_Name", headerName: "Category", width: 160 },
    {
      field: "Sales_Rate",
      headerName: "Sales Rate",
      width: 140,
      valueFormatter: ({ value }) => value,
      align: "right",
    },
    {
      field: "Reorder_Quantity",
      headerName: "Current Stock",
      width: 140,
      align: "right",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={goBack}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 hover:text-gray-900"
              >
                <HiArrowLeft size={20} />
              </button>
              <h1 className="text-xl font-semibold text-gray-900">
                Item Group
              </h1>
              {itemGroupDetails?.Item_Group_Id && (
                <span className="text-sm text-gray-500 font-mono">
                  #{itemGroupDetails.Item_Group_Id}
                </span>
              )}
            </div>

            <div className="flex items-center gap-3">
              {/* Status Toggle */}
              <div className="flex items-center gap-3 px-4 py-2 rounded-lg border border-gray-200 bg-gray-50">
                <span className={`text-sm font-medium ${isActive ? "text-emerald-700" : "text-rose-700"}`}>
                  {isActive ? "Active" : "Inactive"}
                </span>
                <ToggleSwitch
                  toogleItemStatus={toggleGroupStatus}
                  Item_Status_Id={itemGroupDetails?.Item_Group_Status_Id}
                />
              </div>

              <button
                onClick={confirmAndEdit}
                className="p-2.5 rounded-lg hover:bg-gray-100 text-blue-600 hover:text-blue-700 transition-colors"
                title="Edit Group"
              >
                <HiPencil size={20} />
              </button>

              <button
                onClick={goToNewGroup}
                className="inline-flex items-center gap-2 px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors shadow-sm"
              >
                <HiPlus size={18} />
                New Group
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar - Item Group List */}
        <aside className="w-80 xl:w-96 flex-shrink-0 border-r bg-white overflow-hidden">
          <ItemGroupDetailsScroll viewItemDetails={(newId) => {
            navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/item-group-details/${newId}`);
            loadGroupDetails(newId);
          }} />
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto bg-gray-50">
          <div className="max-w-[1400px] mx-auto p-6 lg:p-8">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
              </div>
            ) : (
              <div className="space-y-8">
                {/* Group Info Card */}
                <div className="bg-white rounded-xl border shadow-sm p-6 lg:p-8">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-1">
                        {itemGroupDetails?.Item_Group_Name || "Item Group"}
                      </h2>
                      <p className="text-sm text-gray-600">
                        {tableRows.length} item{tableRows.length !== 1 ? "s" : ""}
                      </p>
                    </div>

                    {/* Images Preview */}
                    {imageList.length > 0 && (
                      <div className="lg:max-w-xs w-full">
                        <ItemGroupImageDisplay
                          imageList={imageList}
                          className="rounded-lg border shadow-sm object-cover"
                        />
                      </div>
                    )}
                  </div>

                  {/* Details Grid */}
                  <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 text-sm">
                    {[
                      { label: "ID", value: itemGroupDetails?.Item_Group_Id },
                      { label: "Category", value: itemGroupDetails?.Item_Group_Category_Name },
                      { label: "Brand", value: itemGroupDetails?.Item_Group_Brand_Name },
                      { label: "Manufacturer", value: itemGroupDetails?.Item_Group_Manufacturer_Name },
                      { label: "Feature", value: itemGroupDetails?.Product_Feature_Name },
                      { label: "Plan", value: itemGroupDetails?.Item_Group_Plan_Name },
                      { label: "Type", value: itemGroupDetails?.Item_Group_Type_Name },
                      { label: "Unit", value: itemGroupDetails?.Item_Group_Unit_Name },
                      { label: "Tax", value: itemGroupDetails?.Item_Group_Tax_Name },
                    ].map((item, idx) => (
                      <div key={idx} className="flex flex-col">
                        <span className="text-sm text-gray-600 font-medium">{item.label}</span>
                        <span className="mt-1 text-base font-medium text-gray-900">
                          {item.value || "—"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Items Table Section */}
                <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
                  <div className="px-6 py-5 border-b bg-gray-50 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-800">
                      Items in Group ({tableRows.length})
                    </h3>
                    <button
                      onClick={() => {/* Add view opening stock logic */}}
                      className="text-sm text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1"
                    >
                      <HiEye size={16} />
                      View Opening Stock
                    </button>
                  </div>

                  <div className="p-1">
                    <DataGrid
                      rows={tableRows}
                      columns={columns}
                      pageSizeOptions={[10, 25, 50]}
                      initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                      pagination
                      slots={{ pagination: CustomPagination }}
                      disableRowSelectionOnClick
                      autoHeight
                      sx={{
                        border: "none",
                        "& .MuiDataGrid-columnHeader": {
                          backgroundColor: "#f9fafb",
                          fontWeight: 600,
                        },
                        "& .MuiDataGrid-cell": {
                          borderBottom: "1px solid #f3f4f6",
                        },
                        "& .MuiDataGrid-row:hover": {
                          backgroundColor: "#f0f9ff",
                        },
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}